import { devLog } from '@/services/logger'

/**
 * Gerenciador centralizado de cache com configurações via .env
 * Controla TTL, limpeza automática e estatísticas de todos os caches da aplicação
 */

interface CacheEntry {
  data: any
  timestamp: number
  ttl: number
}

class CacheManager {
  // ✅ FASE 1: TTL diferenciado por tipo de arquivo (em milissegundos)
  static readonly FILE_CACHE_TTL = (Number(import.meta.env.VITE_FILE_CACHE_TTL) || 10) * 60 * 1000 // 10min default
  static readonly WEBSOCKET_CACHE_TTL = (Number(import.meta.env.VITE_WEBSOCKET_CACHE_TTL) || 15) * 60 * 1000 // 15min default
  static readonly MEDIA_CACHE_TTL = (Number(import.meta.env.VITE_MEDIA_CACHE_TTL) || 5) * 60 * 1000 // 5min default
  static readonly CLEANUP_INTERVAL = (Number(import.meta.env.VITE_CACHE_CLEANUP_INTERVAL) || 5) * 60 * 1000 // 5min default

  // ✅ NOVO: TTL específico por tipo de arquivo
  static readonly IMAGE_CACHE_TTL = (Number(import.meta.env.VITE_IMAGE_CACHE_TTL) || 30) * 60 * 1000 // 30min - imagens ficam mais tempo
  static readonly VIDEO_CACHE_TTL = (Number(import.meta.env.VITE_VIDEO_CACHE_TTL) || 10) * 60 * 1000 // 10min - vídeos ocupam mais espaço
  static readonly AUDIO_CACHE_TTL = (Number(import.meta.env.VITE_AUDIO_CACHE_TTL) || 20) * 60 * 1000 // 20min - áudios intermediário
  static readonly DOCUMENT_CACHE_TTL = (Number(import.meta.env.VITE_DOCUMENT_CACHE_TTL) || 60) * 60 * 1000 // 60min - documentos persistem mais

  private static caches = new Map<string, Map<string, CacheEntry>>()
  private static cleanupIntervals = new Map<string, NodeJS.Timeout>()

  // ✅ NOVO: Cache inteligente para URLs de mídia
  private static blobUrlCache = new Map<string, string>()
  private static urlReferenceCounter = new Map<string, number>()

  /**
   * Obtém o ID da empresa atual do localStorage para escopo de cache
   */
  private static getCompanyId(): string {
    return localStorage.getItem('cmpId') || 'global'
  }

  /**
   * Cria ou obtém cache com TTL específico
   */
  static getCache(name: string, ttl: number = this.FILE_CACHE_TTL): Map<string, CacheEntry> {
    const scopedName = `${this.getCompanyId()}:${name}`
    if (!this.caches.has(scopedName)) {
      this.caches.set(scopedName, new Map())
      this.startCleanupInterval(scopedName, ttl)
    }
    return this.caches.get(scopedName)!
  }

  /**
   * ✅ FASE 1: Define item no cache com TTL inteligente baseado no tipo de arquivo
   */
  static set(cacheName: string, key: string, data: any, customTTL?: number): void {
    const cache = this.getCache(cacheName)
    let ttl = customTTL || this.FILE_CACHE_TTL

    // ✅ TTL inteligente baseado no tipo de arquivo (se não especificado customTTL)
    if (!customTTL && data && typeof data === 'object') {
      if (data.blob && data.blob.type) {
        ttl = this.getTTLByFileType(data.blob.type)
      } else if (data.type) {
        ttl = this.getTTLByFileType(data.type)
      }
    }

    cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    })

    devLog(
      `[CacheManager] Item adicionado ao cache ${cacheName}: ${key} (TTL: ${Math.round(ttl / 60000)}min, tipo: ${
        data?.blob?.type || data?.type || 'unknown'
      })`,
    )
  }

  /**
   * ✅ FASE 1: Determina TTL baseado no tipo MIME do arquivo
   */
  static getTTLByFileType(mimeType: string): number {
    if (!mimeType || typeof mimeType !== 'string') {
      return this.FILE_CACHE_TTL
    }

    const type = mimeType.toLowerCase()

    if (type.startsWith('image/')) {
      return this.IMAGE_CACHE_TTL
    } else if (type.startsWith('video/')) {
      return this.VIDEO_CACHE_TTL
    } else if (type.startsWith('audio/')) {
      return this.AUDIO_CACHE_TTL
    } else if (
      type.includes('pdf') ||
      type.includes('document') ||
      type.includes('text/') ||
      type.includes('application/vnd.') ||
      type.includes('application/msword') ||
      type.includes('application/vnd.openxmlformats')
    ) {
      return this.DOCUMENT_CACHE_TTL
    }

    // Fallback para tipos desconhecidos
    return this.FILE_CACHE_TTL
  }

  /**
   * Obtém item do cache se ainda válido
   */
  static get<T>(cacheName: string, key: string): T | null {
    const cache = this.caches.get(cacheName)
    if (!cache) return null

    const entry = cache.get(key)
    if (!entry) return null

    // Verificar se expirou
    if (Date.now() - entry.timestamp > entry.ttl) {
      // Revogar URL de blob se existir
      if (entry.data?.url && entry.data.url.startsWith('blob:')) {
        URL.revokeObjectURL(entry.data.url)
      }
      cache.delete(key)
      return null
    }

    return entry.data as T
  }

  /**
   * Verifica se item existe e é válido
   */
  static has(cacheName: string, key: string): boolean {
    return this.get(cacheName, key) !== null
  }

  /**
   * Remove item específico
   */
  static delete(cacheName: string, key: string): boolean {
    const cache = this.caches.get(cacheName)
    if (!cache) return false

    // ✅ Limpar blob URL associada se existir
    this.releaseUrl(key)

    const deleted = cache.delete(key)
    if (deleted) {
      devLog(`[CacheManager] Item removido do cache ${cacheName}: ${key}`)
    }
    return deleted
  }

  /**
   * Limpa cache específico
   */
  static clear(cacheName: string): void {
    const cache = this.caches.get(cacheName)
    if (cache) {
      // ✅ Limpar todas as blob URLs antes de limpar o cache
      for (const key of cache.keys()) {
        this.releaseUrl(key)
      }

      cache.clear()
      devLog(`[CacheManager] Cache ${cacheName} foi limpo`)
    }
  }

  /**
   * Limpa todos os caches
   */
  static clearAll(): void {
    for (const cacheName of this.caches.keys()) {
      this.clear(cacheName)
    }
    this.caches.clear()

    // Limpar intervals
    for (const [, interval] of this.cleanupIntervals.entries()) {
      clearInterval(interval)
    }
    this.cleanupIntervals.clear()
    devLog('[CacheManager] Todos os caches foram limpos')
  }

  /**
   * Inicia limpeza automática para um cache
   */
  private static startCleanupInterval(cacheName: string, ttl: number): void {
    if (this.cleanupIntervals.has(cacheName)) return

    const interval = setInterval(() => {
      this.cleanupExpired(cacheName)
    }, this.CLEANUP_INTERVAL)

    this.cleanupIntervals.set(cacheName, interval)
    devLog(
      `[CacheManager] Cleanup automático iniciado para ${cacheName} (TTL: ${Math.round(
        ttl / 60000,
      )}min, Interval: ${Math.round(this.CLEANUP_INTERVAL / 60000)}min)`,
    )
  }

  /**
   * Remove itens expirados de um cache específico
   */
  private static cleanupExpired(cacheName: string): void {
    const cache = this.caches.get(cacheName)
    if (!cache) return

    const now = Date.now()
    let removedCount = 0

    for (const [key, entry] of cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        // Revogar URL de blob se existir
        if (entry.data?.url && entry.data.url.startsWith('blob:')) {
          URL.revokeObjectURL(entry.data.url)
        }
        cache.delete(key)
        removedCount++
      }
    }

    if (removedCount > 0) {
      devLog(`[CacheManager] Limpeza do cache ${cacheName}: ${removedCount} itens removidos`)
    }
  }

  /**
   * Obtém estatísticas dos caches
   */
  static getStats(): Record<string, any> {
    const stats: Record<string, any> = {}

    for (const [name, cache] of this.caches.entries()) {
      const entries = Array.from(cache.values())
      const totalSize = entries.reduce((acc, entry) => {
        return acc + (entry.data?.size || 0)
      }, 0)

      stats[name] = {
        count: cache.size,
        totalSize: this.formatBytes(totalSize),
        totalSizeBytes: totalSize,
        oldestEntry:
          entries.length > 0 ? new Date(Math.min(...entries.map((e) => e.timestamp))).toLocaleString() : null,
        newestEntry:
          entries.length > 0 ? new Date(Math.max(...entries.map((e) => e.timestamp))).toLocaleString() : null,
      }
    }

    return stats
  }

  /**
   * Força limpeza de todos os caches expirados
   */
  static forceCleanup(): void {
    for (const cacheName of this.caches.keys()) {
      this.cleanupExpired(cacheName)
    }
  }

  /**
   * Cria URL persistente baseada no tipo de mídia
   * Para imagens: Base64 (persiste indefinidamente)
   * Para vídeos/áudios: Blob URL gerenciada (revogação controlada)
   */
  static createPersistentUrl(fileId: string, blob: Blob): Promise<string> {
    const isImage = blob.type.startsWith('image/')

    if (isImage) {
      // Imagens: Base64 (persiste mesmo após refresh)
      return this.blobToBase64(blob)
    } else {
      // Vídeos/Áudios: Blob URL gerenciada com referência
      const scopedFileId = `${this.getCompanyId()}:${fileId}`
      const existingUrl = this.blobUrlCache.get(scopedFileId)

      if (existingUrl) {
        // Incrementar contador de referências
        const currentRefs = this.urlReferenceCounter.get(scopedFileId) || 0
        this.urlReferenceCounter.set(scopedFileId, currentRefs + 1)
        return Promise.resolve(existingUrl)
      }

      const blobUrl = URL.createObjectURL(blob)
      this.blobUrlCache.set(scopedFileId, blobUrl)
      this.urlReferenceCounter.set(scopedFileId, 1)

      devLog(`[CacheManager] Blob URL criada para ${scopedFileId}: ${blobUrl}`)
      return Promise.resolve(blobUrl)
    }
  }

  /**
   * Recupera URL do cache ou recria se necessário
   */
  static getOrRecreateUrl(fileId: string): Promise<string | null> {
    // Verificar cache de blob URLs primeiro
    const scopedFileId = `${this.getCompanyId()}:${fileId}`
    const cachedUrl = this.blobUrlCache.get(scopedFileId)
    if (cachedUrl) {
      return Promise.resolve(cachedUrl)
    }

    // Verificar cache de blobs e recriar URL
    const cached = this.get<{ blob: Blob }>('websocket-files', fileId) || this.get<{ blob: Blob }>('chat-files', fileId)

    if (cached) {
      return this.createPersistentUrl(fileId, cached.blob)
    }

    return Promise.resolve(null)
  }

  /**
   * Decrementa referência e revoga URL se não está mais em uso
   */
  static releaseUrl(fileId: string): void {
    const scopedFileId = `${this.getCompanyId()}:${fileId}`
    const currentRefs = this.urlReferenceCounter.get(scopedFileId) || 0
    const newRefs = Math.max(0, currentRefs - 1)

    if (newRefs === 0) {
      const url = this.blobUrlCache.get(scopedFileId)
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url)
        this.blobUrlCache.delete(scopedFileId)
        this.urlReferenceCounter.delete(scopedFileId)
        devLog(`[CacheManager] Blob URL revogada para ${scopedFileId}: ${url}`)
      }
    } else {
      this.urlReferenceCounter.set(scopedFileId, newRefs)
    }
  }

  /**
   * Força revogação de URL específica (para limpeza)
   */
  static forceRevokeUrl(fileId: string): void {
    const scopedFileId = `${this.getCompanyId()}:${fileId}`
    const url = this.blobUrlCache.get(scopedFileId)
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url)
      this.blobUrlCache.delete(scopedFileId)
      this.urlReferenceCounter.delete(scopedFileId)
      devLog(`[CacheManager] Blob URL forçadamente revogada para ${scopedFileId}: ${url}`)
    }
  }

  /**
   * Converte Blob para Base64
   */
  private static blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  /**
   * ✅ FASE 1: Obtém informações de configuração com novos TTLs
   */
  static getConfig(): Record<string, any> {
    return {
      FILE_CACHE_TTL_MINUTES: Math.round(this.FILE_CACHE_TTL / 60000),
      WEBSOCKET_CACHE_TTL_MINUTES: Math.round(this.WEBSOCKET_CACHE_TTL / 60000),
      MEDIA_CACHE_TTL_MINUTES: Math.round(this.MEDIA_CACHE_TTL / 60000),
      IMAGE_CACHE_TTL_MINUTES: Math.round(this.IMAGE_CACHE_TTL / 60000),
      VIDEO_CACHE_TTL_MINUTES: Math.round(this.VIDEO_CACHE_TTL / 60000),
      AUDIO_CACHE_TTL_MINUTES: Math.round(this.AUDIO_CACHE_TTL / 60000),
      DOCUMENT_CACHE_TTL_MINUTES: Math.round(this.DOCUMENT_CACHE_TTL / 60000),
      CLEANUP_INTERVAL_MINUTES: Math.round(this.CLEANUP_INTERVAL / 60000),
      ACTIVE_CACHES: Array.from(this.caches.keys()),
      CLEANUP_INTERVALS_ACTIVE: this.cleanupIntervals.size,
    }
  }

  private static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }
}

export default CacheManager
